import { useEffect, useState } from 'react'
import { Spin, Pagination, Empty } from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { getStockQuarterlyFinancials } from '@/api/stock'
import type { StockFinancialData } from '@/types/stockPool'

const reportTypeLabel: Record<string, string> = {
    quarterly_q1: 'Q1', quarterly_q2: 'Q2',
    quarterly_q3: 'Q3', quarterly_q4: 'Q4',
}

function fmtBig(v: number | null | undefined) {
    if (v == null) return '—'
    if (Math.abs(v) >= 1e8) return (v / 1e8).toFixed(1) + '亿'
    if (Math.abs(v) >= 1e4) return (v / 1e4).toFixed(1) + '万'
    return v.toFixed(2)
}

function PctSpan({ v }: { v: number | null | undefined }) {
    if (v == null) return <span style={{ color: 'var(--text-3)' }}>—</span>
    return <span className={v >= 0 ? 'pos' : 'neg'}>{v >= 0 ? '+' : ''}{v.toFixed(1)}%</span>
}

const DEFAULT_PAGE_SIZE = 10

export default function QuarterlyFinancials() {
    const navigate = useNavigate()
    const { code } = useParams<{ code: string }>()
    const [items, setItems] = useState<StockFinancialData[]>([])
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [pageSize] = useState(DEFAULT_PAGE_SIZE)
    const [total, setTotal] = useState(0)

    const toQuarterlyReport = (id: number) =>
        navigate(`/stock-kb/${encodeURIComponent(code ?? '')}/quarterly-report/${id}`)

    useEffect(() => {
        if (!code) return
        setLoading(true)
        getStockQuarterlyFinancials({
            code: decodeURIComponent(code),
            page,
            page_size: pageSize,
        })
            .then(res => {
                setItems(res.items)
                setTotal(res.total)
            })
            .catch(() => {
                setItems([])
                setTotal(0)
            })
            .finally(() => setLoading(false))
    }, [code, page, pageSize])

    if (loading) return <div style={{ padding: '60px 0', textAlign: 'center' }}><Spin /></div>

    if (!items.length) {
        return <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <Empty description="暂无季度财务数据" />
        </div>
    }

    return (
        <div className="skd__fin-list">
            <div className="skd__fin-list-header">
                <span className="skd__fin-list-title">季度财务数据</span>
                <span className="skd__fin-list-count">{total} 条</span>
            </div>
            <div className="skd__fin-list-table-wrap">
                <table className="skd__fin-list-table">
                    <thead>
                        <tr>
                            <th>报告期</th>
                            <th>类型</th>
                            <th>报告日期</th>
                            <th>营业收入</th>
                            <th>收入 YoY</th>
                            <th>归母净利润</th>
                            <th>利润 YoY</th>
                            <th>毛利率</th>
                            <th>净利率</th>
                            <th>PE(TTM)</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(ind => {
                            const rt = (ind as any).report_type ?? ''
                            const typeLabel = reportTypeLabel[rt] ?? rt ?? '—'
                            return (
                                <tr key={ind.id} onClick={() => toQuarterlyReport(ind.id)} className="skd__fin-list-row">
                                    <td className="skd__fin-list-period">{(ind as any).fiscal_year ?? '—'}</td>
                                    <td>
                                        <span className="skd__fin-list-type" style={{ background: '#eab30820', color: '#eab308' }}>
                                            {typeLabel}
                                        </span>
                                    </td>
                                    <td className="skd__fin-list-date">{ind.report_date}</td>
                                    <td className="skd__fin-list-num">{fmtBig(ind.revenue)}</td>
                                    <td><PctSpan v={ind.revenue_yoy_pct} /></td>
                                    <td className="skd__fin-list-num">{fmtBig(ind.net_profit_parent)}</td>
                                    <td><PctSpan v={ind.adjusted_net_profit_yoy_pct} /></td>
                                    <td className="skd__fin-list-num">{ind.gross_margin_pct != null ? ind.gross_margin_pct.toFixed(1) + '%' : '—'}</td>
                                    <td className="skd__fin-list-num">{ind.net_margin_pct != null ? ind.net_margin_pct.toFixed(1) + '%' : '—'}</td>
                                    <td className="skd__fin-list-num">{ind.pe_ttm && ind.pe_ttm > 0 ? ind.pe_ttm.toFixed(1) + 'x' : '—'}</td>
                                    <td><span className="skd__fin-list-btn">查看全部字段 →</span></td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            <div className="skd__fin-list-pagination">
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={total}
                    showSizeChanger={false}
                    showTotal={t => `共 ${t} 条`}
                    onChange={(p) => setPage(p)}
                />
            </div>
        </div>
    )
}
